import { yupResolver } from "@hookform/resolvers/yup";
import { MolhamLanguagesObject } from "apiTypes";
import MolhamModalOffcanvas from "components/MolhamModalOffCanvas";
import BackBtn from "components/ReuseableCompos/BackBtn";
import MolhamButton from "components/ReuseableCompos/MolhamButton";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { memo, useImperativeHandle, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import useModal from "../../customHooks/useModal";
import useUtilities from "../../customHooks/useUtilities";
import { addDays } from "../../utils/dateHelpers";
import { isEmpty } from "../../utils/isEmpty";
import CustomModalHeader from "../CustomModalHeader";
import Step1 from "./Steps/Step1";
import Step2 from "./Steps/Step2";
import Step3 from "./Steps/Step3";
import Step4 from "./Steps/Step4";
import Step5 from "./Steps/Step5";

const getStepContent = (
  step: number,
  data: {
    id: number;
    name: MolhamLanguagesObject;
  }[]
) => {
  if (!data) {
    return null;
  }
  switch (step) {
    case 0:
      return <Step1 data={data} />;
    case 1:
      return <Step2 isProfile={false} />;
    case 2:
      return <Step3 />;
    case 3:
      return <Step4 />;
    case 4:
      return <Step5 />;
    default:
      return "Unknown step";
  }
};

const CreateModal = ({
  showCreateModal,
  handleCloseCreateModal,
  onFormSubmit,
  action,
  noHeader,
  noBorder,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    "fundraiser",
    "fundraiser",
    "fundraiser",
    "fundraiser",
    "fundraiser",
  ];
  const { data: utilities } = useUtilities();

  const { asPath } = useRouter();
  const currentPath = asPath.split("/")[1];
  const singularPath = currentPath.slice(0, currentPath.length - 1);
  const data = useMemo(
    () => utilities?.target_types[singularPath]["categories"],
    [utilities, singularPath]
  );

  const { t } = useTranslation("common");

  // handling default values
  const defaultValues = {
    title: "",
    ["category_id"]: "1",
    ["date"]: addDays(new Date(), -2)?.toISOString().split("T")[0],
    details: "",
    preview_image: "",
    private: false,
    approved_terms: false,
  };

  const handleReset = () => {
    setActiveStep(0);
    reset();
  };

  useImperativeHandle(
    action,
    () => ({
      handleReset,
    }),

    [handleReset]
  );

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onFormSubmit(data);
    } catch (err) {}
    setIsLoading(false);
  };

  const validationSchema = [
    //validation for step1
    yup.object({
      category_id: yup.string().required(t(`field_required`)),
      title: yup
        .string()
        .min(10, t("min_x_invalid", { x: t("10") }))
        .max(250, t("max_x_invalid", { x: t("250") }))
        .required(t(`field_required`)),
      // lastName: yup.string().required(),
    }),
    //validation for step2
    yup.object({
      date: yup.date().required(t("field_required")),

      details: yup
        .string()
        .min(30, t("min_x_invalid", { x: t("30") }))
        .max(250, t("max_x_invalid", { x: t("250") }))
        .required(t(`field_required`)),
    }),
    //validation for step3
    yup.object({
      preview_image: yup.mixed(),
      // .not([""], t("field_required")),
    }),
    //validation for step4
    yup.object({
      private: yup.bool().required(t("field_required")),
    }),
    //validation for step5
    yup.object({
      approved_terms: yup.bool().oneOf([true], t("field_required")),
    }),
  ];
  const currentValidationSchema = validationSchema[activeStep];
  const methods = useForm({
    shouldUnregister: false,
    defaultValues,
    resolver: yupResolver(currentValidationSchema),

    reValidateMode: "onSubmit",
  });
  const { handleSubmit, reset, trigger } = methods;

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const {
    formState: { errors, isDirty },
  } = methods;
  const {
    showModal: showConfirmModal,
    handleCloseModal: handleCloseConfirmModal,
    handleShowModal: handleShowConfirmModal,
  } = useModal();
  const closeBothAndReset = () => {
    handleCloseConfirmModal();
    handleReset();
    handleCloseCreateModal();
  };
  return (
    <MolhamModalOffcanvas
      size="lg"
      fullscreen
      swipeToClose={false}
      header={{
        label: t("add_event_title"),
        customButton: <BackBtn onBackBtnClick={handleBack} />,
        showCustomBtnCond: activeStep !== 0,
      }}
      onHide={handleShowConfirmModal}
      show={showCreateModal}
    >
      <Modal
        size="sm"
        backdrop="static"
        centered
        show={showConfirmModal}
        onHide={handleCloseConfirmModal}
      >
        {!noHeader && (
          <CustomModalHeader onCloseBtnClick={handleCloseConfirmModal}>
            {t("close_create")}
          </CustomModalHeader>
        )}
        <div className={`${noBorder ? "" : "border-bottom"}  p-3`}>
          <div className="text-center lh17">
            {t("are_you_sure_close_create")}
          </div>
        </div>
        <div className="d-flex pb-3 px-3 align-items-center justify-content-between">
          <Button
            variant="secondary"
            style={{ width: "49%" }}
            onClick={handleCloseConfirmModal}
          >
            {t("resume")}
          </Button>
          <Button
            variant="danger"
            style={{ width: "49%" }}
            onClick={closeBothAndReset}
          >
            {t("cancel")}
          </Button>
        </div>
      </Modal>

      <FormProvider {...methods}>
        <form
          className={`${!isEmpty(errors) ? "invalid" : ""}`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div>{getStepContent(activeStep, data)}</div>
          <div className="d-flex justify-content-center">
            {activeStep === steps.length - 1 ? (
              <MolhamButton
                id="event-create-btn"
                disabled={!isDirty || !methods.watch("approved_terms")}
                className="w-100"
                label={t(`create`)}
                isLoading={isLoading}
                startIcon="fe-plus"
              />
            ) : (
              <MolhamButton
                id="event-next-btn"
                className="w-100"
                label={t(`next`)}
                endIcon="fe-arrow-right"
                type="button"
                disabled={
                  activeStep === 1 &&
                  (methods.watch("details").length <= 30 ||
                    methods.watch("details").length >= 250)
                }
                onClick={handleNext}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </MolhamModalOffcanvas>
  );
};

export default memo(CreateModal);
